import React, { memo, useEffect, useMemo, useState } from 'react';
import { Box, Table, TableBody } from '@mui/material';

import { BasicLayout, CardWrapper } from '@monorepo/common';

import { IJobPackagesResponse } from '../../../../services/servicePrepaidPurchase';
import PanelTableHeader from '../../components/PanelTableHeader';
import { IStepsOneInterfaces } from '../../stepsOneInterfaces';
import { Scrollbar } from '../../../../containers/ScrollBar';
import Steps1TableBodyCells from './Steps1TableBodyCells';
import SinglePostPromo from '../../components/SinglePostPromo';
import { PromoCodeField } from '../../../../components/PromoCodeField';
import { TOKENS } from '../../../../styles/tokens';
import { IEventsStepProps } from '../../interfaceSteps';

interface IStep1Props extends IEventsStepProps{
  list: IStepsOneInterfaces[],
  Nav: React.ElementType,
  setPackages: any,
  jobPackages?: IJobPackagesResponse,
  couponCode?: string,
  isCouponLoading?: boolean,
  isCouponFetching?: boolean,
  onApplyCoupon?: (code: string | undefined) => void
}
export const Step1 = memo(({
  onNext, list, setPackages, Nav, jobPackages, couponCode, isCouponLoading, isCouponFetching, onApplyCoupon = () => {},
}:IStep1Props) => {
  const tableList: string[] = useMemo(() => ['Quantity', 'Description', 'Listings', 'Price'], []);

  const [sumListings, setSumListings] = useState<number>(0);
  const [sumPrice, setSumPrice] = useState(0);

  useEffect(() => {
    setSumListings(list.reduce((a, b) => a + (b.number * b.listingsNumber), 0));
    setSumPrice(list.reduce((a, b) => a + (b.number * b.itemPrice), 0));
  }, [list]);

  return (
    <BasicLayout dataCy="step1">
      <CardWrapper
        classNameForInternalBlock="cpg-step-card cpg-step-card--flush"
        isLoading={isCouponLoading}
        isNoPadding
      >
        <Scrollbar>
          <Table sx={{ minWidth: 680 }}>
            <PanelTableHeader tableList={tableList} />
            <TableBody>
              <Steps1TableBodyCells
                list={list}
                setPackages={setPackages}
              />
            </TableBody>
          </Table>
        </Scrollbar>
      </CardWrapper>

      {/* Checkout footer (mockup): the Code box sits on the SAME line as the order
          total + Next button on desktop, and stacks on narrow screens. The grid's
          old TOTAL row moved here so the amount sits right beside the CTA. */}
      <Box
        className="cpg-checkout-footer"
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2.5,
          mt: 3,
          pt: 3,
          borderTop: `1px solid ${TOKENS.lineSoft}`,
          '@media (max-width:700px)': {
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        }}
      >
        <PromoCodeField
          initialCode={couponCode}
          couponApplied={jobPackages?.couponApplied}
          couponCode={jobPackages?.couponCode}
          couponErrorMessage={jobPackages?.couponErrorMessage}
          isFetching={isCouponFetching}
          onApply={onApplyCoupon}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            '@media (max-width:700px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 1.5,
              width: '100%',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              whiteSpace: 'nowrap',
              '@media (max-width:700px)': {
                flexDirection: 'row',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                width: '100%',
              },
            }}
          >
            <Box
              sx={{
                fontFamily: TOKENS.fontDisplay,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: TOKENS.placeholder,
              }}
            >
              Total
            </Box>
            <Box
              data-cy="footerTotalPrice"
              sx={{
                fontFamily: TOKENS.fontDisplay,
                fontSize: 24,
                fontWeight: 800,
                lineHeight: 1.1,
                color: TOKENS.ink,
              }}
            >
              $
              {sumPrice}
            </Box>
          </Box>

          <Box
            className="cpg-checkout-footer__nav"
            sx={{
              '& > div': { mt: 0 },
              '@media (max-width:700px)': { '& button': { width: '100%' } },
            }}
          >
            <Nav
              onNext={onNext}
              nextDisabled={sumListings === 0}
            />
          </Box>
        </Box>
      </Box>

      <SinglePostPromo />
    </BasicLayout>
  );
});
